import { useCallback, useMemo } from 'react';
import { CustomerPermissionOnly, CustomerRoleOnly } from '@/api/apis/customer';
import { RolesPermissions } from '@/types/permissions';
import { getVisiblePermissions, isPermissionNonAssignable } from '@/utils/helpers';
import {
  DEFAULT_EDIT_PERMISSION_ROLES,
  hasPermissionsChanged,
  isPermissionAllowed,
  isPermissionMandatory,
  resolvePermissionsForRoles,
  resolveRoleConflicts,
} from './utils';

export const useRoleRestrictions = ({
  roleValues,
  permissionValues,
  updateRoles,
  updatePermissions,
  canEditPermissionRoles, // some permissions are displayed as disabled and are forbidden to edit
  trigger,
}: {
  roleValues: CustomerRoleOnly[];
  permissionValues: CustomerPermissionOnly[];
  updateRoles: (roles: CustomerRoleOnly[]) => void;
  updatePermissions: (permissions: CustomerPermissionOnly[]) => void;
  canEditPermissionRoles?: RolesPermissions;
  trigger: () => Promise<[boolean, boolean]>;
}) => {
  const editPermissionRoles = useMemo(
    () => canEditPermissionRoles ?? DEFAULT_EDIT_PERMISSION_ROLES,
    [canEditPermissionRoles],
  );

  const isPermissionMandatoryForActiveRole = useCallback(
    (permission: CustomerPermissionOnly) => isPermissionMandatory(permission, roleValues),
    [roleValues],
  );

  const isPermissionSelectionForbiddenForRole = useCallback(
    (permission: CustomerPermissionOnly) => !isPermissionAllowed(permission, roleValues),
    [roleValues],
  );

  // Hide non-assignable permissions the user doesn't already hold; keep (disabled) the ones they do.
  const getDisplayablePermissions = useCallback(
    (permissionsList: { name: string; value: CustomerPermissionOnly }[]) =>
      getVisiblePermissions(permissionsList, permissionValues),
    [permissionValues],
  );

  const handleRoleChange = useCallback(
    (newRoles: CustomerRoleOnly[]) => {
      const { finalRoles, addedRole } = resolveRoleConflicts(newRoles, roleValues);
      const rolesChanged = JSON.stringify(finalRoles) !== JSON.stringify(roleValues);
      if (rolesChanged) updateRoles(finalRoles);

      const finalPermissions = resolvePermissionsForRoles(finalRoles, addedRole, permissionValues, editPermissionRoles);
      const permissionsChanged = hasPermissionsChanged(finalPermissions, permissionValues);
      if (permissionsChanged) updatePermissions(finalPermissions);

      if (rolesChanged || permissionsChanged) trigger();
    },
    [roleValues, permissionValues, updateRoles, updatePermissions, editPermissionRoles, trigger],
  );

  return {
    handleRoleChange,
    isPermissionMandatoryForActiveRole,
    isPermissionSelectionForbiddenForRole,
    getDisplayablePermissions,
    isPermissionNonAssignable,
  };
};
